import { useState, useEffect, useRef } from "react";

const BAR_DURATIONS = ["0.45s", "0.60s", "0.35s", "0.55s", "0.42s"];
const BAR_HEIGHTS = [6, 12, 9, 14, 7];

const DEFAULT_PLAYLIST = [
  { title: "diedlonely", artist: "Bipolar", src: "/classic/diedlonely - bipolar.mp3" },
];

export default function FloatingMusicPlayer({
  liveLabel = "Live · now playing",
  pauseLabel = "Live · tap to unmute",
  accentColor = "#e53935",
  playlist = DEFAULT_PLAYLIST,
  dropdownDirection = "up", // "up" | "down"
}) {
  const [playing, setPlaying] = useState(false);
  const [, setProgress] = useState(0);
  const [trackIndex, setTrackIndex] = useState(0);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);
  const rootRef = useRef(null);

  const track = playlist[trackIndex] ?? playlist[0];

  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        setProgress((p) => (p + 0.15) % 100);
      }, 300);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [playing]);

  // Sync the actual <audio> element with the playing state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.play().catch(() => {
        // Browsers block autoplay without a user gesture - fall back to paused UI
        setPlaying(false);
      });
    } else {
      audio.pause();
    }
  }, [playing]);

  // When the selected track changes, load it and (if we were playing) resume playback
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.load();
    if (playing) {
      audio.play().catch(() => setPlaying(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackIndex]);

  // Close the dropdown on outside click
  useEffect(() => {
    if (!showPlaylist) return;
    const handleClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setShowPlaylist(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showPlaylist]);

  const toggle = () =>
    setPlaying((p) => {
      const next = !p;
      if (!next) setShowPlaylist(false); // muting closes any open dropdown
      return next;
    });

  const selectTrack = (i) => {
    setTrackIndex(i);
    setShowPlaylist(false);
    setPlaying(true);
  };

  return (
    <div
      ref={rootRef}
      className="nmg-floating-player"
      style={{
        position: "relative",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      }}
    >
      {/* Playlist dropdown */}
      {showPlaylist && (
        <div
          style={{
            position: "absolute",
            [dropdownDirection === "up" ? "bottom" : "top"]: "calc(100% + 8px)",
            right: 0,
            minWidth: 220,
            maxWidth: 280,
            maxHeight: 260,
            overflowY: "auto",
            background: "rgba(15, 15, 17, 0.92)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            border: "1px solid rgba(255,255,255,0.09)",
            borderRadius: 14,
            boxShadow: "0 14px 40px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.4)",
            padding: 6,
            boxSizing: "border-box",
            zIndex: 20,
          }}
        >
          {playlist.map((t, i) => {
            const active = i === trackIndex;
            return (
              <div
                key={t.src}
                onClick={() => selectTrack(i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "8px 10px",
                  borderRadius: 9,
                  cursor: "pointer",
                  background: active ? "rgba(255,255,255,0.06)" : "transparent",
                  borderLeft: `2px solid ${active ? accentColor : "transparent"}`,
                  transition: "background 150ms ease",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
              >
                {active && playing ? (
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 11, flexShrink: 0, width: 11 }}>
                    {[0, 1, 2].map((b) => (
                      <div
                        key={b}
                        style={{
                          width: 2,
                          height: "100%",
                          background: accentColor,
                          borderRadius: 1,
                          transformOrigin: "bottom",
                          animation: `nmg-bounce ${0.4 + b * 0.12}s ease-in-out infinite alternate`,
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <span style={{ width: 11, fontSize: 9, color: "rgba(255,255,255,0.35)", flexShrink: 0 }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                )}
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25, overflow: "hidden" }}>
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: active ? "#fff" : "rgba(255,255,255,0.85)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t.title}
                  </span>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 500,
                      color: "rgba(255,255,255,0.5)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t.artist}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div
        onClick={toggle}
        aria-label={playing ? pauseLabel : liveLabel}
        style={{
          display: "flex",
          alignItems: "center",
          gap: playing ? 8 : 10,
          height: 58,
          boxSizing: "border-box",
          padding: "0 12px 0 7px",
          background: "rgba(15, 15, 17, 0.85)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(255,255,255,0.09)",
          transition:
            "gap 900ms cubic-bezier(0.22, 1, 0.36, 1), padding 900ms cubic-bezier(0.22, 1, 0.36, 1)",
          borderRadius: 999,
          cursor: "pointer",
          color: "#fff",
          boxShadow: "0 14px 40px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.4)",
          userSelect: "none",
        }}
      >
        {/* Hidden audio element driving actual playback */}
        <audio ref={audioRef} src={track.src} loop preload="auto" />

        {/* CD Disc */}
        <div
          style={{
            position: "relative",
            width: 44,
            height: 44,
            borderRadius: "50%",
            flexShrink: 0,
            overflow: "hidden",
            boxShadow: `0 0 22px ${accentColor}66, 0 0 8px ${accentColor}55, inset 0 0 0 1px rgba(200,200,205,0.35)`,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              backgroundImage: [
                "radial-gradient(ellipse 22% 14% at 42% 38%, rgba(255,255,255,0.55) 0%, rgba(230,230,235,0.25) 40%, rgba(200,200,205,0) 80%)",
                "linear-gradient(135deg, rgba(120,120,125,0) 38%, rgba(110,110,115,0.35) 45%, rgba(135,135,140,0.78) 48%, rgba(170,170,178,0.92) 49.6%, rgba(195,195,202,0.98) 50%, rgba(165,165,172,0.92) 50.6%, rgba(120,120,128,0.78) 52%, rgba(95,95,102,0.35) 55%, rgba(80,80,86,0) 62%)",
                "linear-gradient(135deg, transparent 52%, rgba(20,8,8,0.35) 55%, rgba(20,8,8,0) 63%)",
                "radial-gradient(ellipse 58% 72% at 8% 58%, rgba(35,8,8,0.78) 0%, rgba(55,15,15,0.5) 28%, rgba(70,20,18,0.22) 50%, rgba(70,20,18,0) 70%)",
                "radial-gradient(ellipse 82% 72% at 78% 78%, #ffc070 0%, #ffa052 12%, #f5803a 28%, #dd6428 45%, rgba(195,75,30,0.55) 60%, rgba(150,50,25,0.2) 75%, rgba(150,50,25,0) 85%)",
                "radial-gradient(ellipse 115% 88% at 50% -5%, #ff2418 0%, #ff0a04 18%, #ff0202 38%, #f00604 52%, #d4080a 68%, rgba(170,8,8,0) 85%)",
                "repeating-radial-gradient(circle at 50% 50%, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, rgba(0,0,0,0.10) 1px, rgba(0,0,0,0.10) 2px)",
                "linear-gradient(180deg, #d40c08 0%, #b80a0a 35%, #a0090a 60%, #7a0808 85%, #5e0606 100%)",
              ].join(", "),
              backgroundSize: "100% 100%",
              backgroundPosition: "center",
              animation: "nmg-spin 6s linear infinite",
              animationPlayState: playing ? "running" : "paused",
              willChange: "transform",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: 13,
              height: 13,
              transform: "translate(-50%, -50%)",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 38% 32%, #8a8a90 0%, #5a5a60 55%, #3a3a3e 100%)",
              boxShadow:
                "0 0 0 1px rgba(255,255,255,0.18), inset 0 0 3px rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "#050505",
                boxShadow: "inset 0 0 2px rgba(0,0,0,1)",
              }}
            />
          </div>
        </div>

        {/* Bars + Info */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            overflow: "hidden",
            maxWidth: playing ? 110 : 0,
            opacity: playing ? 1 : 0,
            marginRight: playing ? 0 : -10,
            transition:
              "max-width 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 600ms cubic-bezier(0.22, 1, 0.36, 1) 100ms, margin-right 900ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 2.5,
              height: 16,
              flexShrink: 0,
            }}
          >
            {BAR_HEIGHTS.map((h, i) => (
              <div
                key={i}
                style={{
                  width: 2.5,
                  height: h * 1.4 * 0.7,
                  background: accentColor,
                  borderRadius: 1.25,
                  transformOrigin: "bottom",
                  animation: playing
                    ? `nmg-bounce ${BAR_DURATIONS[i]} ease-in-out infinite alternate`
                    : "none",
                }}
              />
            ))}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "-0.01em",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {track.artist}
            </span>
            <span
              style={{
                fontSize: 8.5,
                fontWeight: 500,
                color: "rgba(255,255,255,0.6)",
                letterSpacing: "0.02em",
                marginTop: 1,
              }}
            >
              now playing
            </span>
          </div>
        </div>

        {/* Volume icon */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          aria-label={playing ? "Mute" : "Unmute"}
          style={{
            background: "transparent",
            border: "none",
            color: "rgba(255,255,255,0.85)",
            cursor: "pointer",
            padding: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {playing ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="22" y1="9" x2="16" y2="15" />
              <line x1="16" y1="9" x2="22" y2="15" />
            </svg>
          )}
        </button>

        {/* Playlist toggle — fades/collapses in sync with the artist info, hidden while muted */}
        {playlist.length > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowPlaylist((s) => !s);
            }}
            aria-label="Toggle playlist"
            aria-expanded={showPlaylist}
            style={{
              background: "transparent",
              border: "none",
              color: "rgba(255,255,255,0.85)",
              cursor: "pointer",
              padding: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              maxWidth: playing ? 22 : 0,
              opacity: playing ? 1 : 0,
              pointerEvents: playing ? "auto" : "none",
              transition:
                "max-width 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 500ms ease",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: showPlaylist
                  ? "rotate(180deg)"
                  : dropdownDirection === "up"
                  ? "rotate(180deg) scaleY(-1)"
                  : "none",
                transition: "transform 250ms ease",
              }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}
      </div>

      <style>{`
        @keyframes nmg-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes nmg-bounce {
          from { transform: scaleY(0.25); }
          to   { transform: scaleY(1); }
        }
        @keyframes nmg-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(0.7); }
        }

        @media (max-width: 768px) {
          .nmg-floating-player {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
